import { ColumnDef } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { PiMagnifyingGlass } from 'react-icons/pi'

import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import Checkbox from 'components/Checkbox/checkbox'
import {
  type EffectivePreferenceSetting,
  useGetEffectiveNotificationPreferencesByKind,
  useUpdateAllNotificationPreferencesByKind,
  useUpdateNotificationPreference,
} from 'services/notifications'

export function NotificationPreferencesTable({ tab }: { tab: string }) {
  const { data: rawData, isLoading } =
    useGetEffectiveNotificationPreferencesByKind({ kind_slug: tab })
  const [searchQuery, setSearchQuery] = useState('')

  // Client-side search: all data is loaded at once (no pagination), so filtering in-memory is sufficient.
  const data = useMemo(() => {
    if (!searchQuery || !rawData) return rawData
    const q = searchQuery.toLowerCase()
    return rawData.filter((item) =>
      item.preference_description?.toLowerCase().includes(q)
    )
  }, [rawData, searchQuery])
  const { mutate: mutateUpdateAllNotificationsPreferencesByKind } =
    useUpdateAllNotificationPreferencesByKind()
  const { mutate: mutateUpdateNotificationsPreferences } =
    useUpdateNotificationPreference()

  const handleCheckAll = async (data: {
    emailEnabled?: boolean
    inAppEnabled?: boolean
  }) => {
    mutateUpdateAllNotificationsPreferencesByKind({
      kind_slug: tab,
      data: {
        email_enabled: data.emailEnabled,
        in_app_enabled: data.inAppEnabled,
      },
    })
  }

  const handleCheckOne = async (
    preferenceSlug: string,
    data: {
      emailEnabled?: boolean
      inAppEnabled?: boolean
    }
  ) => {
    mutateUpdateNotificationsPreferences({
      kind_slug: tab,
      data: {
        preference: preferenceSlug,
        email_enabled: data.emailEnabled,
        in_app_enabled: data.inAppEnabled,
      },
    })
  }

  const columns = useMemo<ColumnDef<EffectivePreferenceSetting, any>[]>(() => {
    return [
      {
        header: 'Notification',
        size: 500,
        meta: { isFirstColumn: true },
        cell: ({ row }) => {
          return row.original?.preference_description
        },
      },
      {
        id: 'all-email',
        size: 140,
        header: () => {
          const allChecked = rawData
            ?.filter((row) => row.is_enabled_email)
            .every((row) => row.email_enabled)
          return (
            <div className="flex items-center gap-2">
              <Checkbox
                aria-label="Toggle email for all"
                checked={allChecked}
                onChange={(e) =>
                  handleCheckAll({
                    emailEnabled: e.target.checked,
                  })
                }
              />
              <span>Email</span>
            </div>
          )
        },
        cell: ({ row }) => {
          if (row.original.is_enabled_email) {
            return (
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={row.original.email_enabled}
                  onChange={(e) =>
                    handleCheckOne(row.original.preference_slug, {
                      emailEnabled: e.target.checked,
                    })
                  }
                />
                {/* Invisible spacer matches the header's label width so the
                    body checkbox lines up with the header checkbox. */}
                <span className="invisible" aria-hidden>
                  Email
                </span>
              </div>
            )
          }
          return null
        },
      },
      {
        id: 'all-in-app',
        size: 140,
        meta: {
          isLastColumn: true,
        },
        header: () => {
          const allChecked = rawData
            ?.filter((row) => row.is_enabled_in_app)
            .every((row) => row.in_app_enabled)
          return (
            <div className="flex items-center gap-2">
              <Checkbox
                aria-label="Toggle in-app for all"
                checked={allChecked}
                onChange={(e) =>
                  handleCheckAll({ inAppEnabled: e.target.checked })
                }
              />
              <span>In App</span>
            </div>
          )
        },
        cell: ({ row }) => {
          if (row.original.is_enabled_in_app) {
            return (
              // The In App column header is centered by the global
              // last-child rule in main.css (originally for Actions
              // icon columns). Center the body cell to match, so the
              // body checkbox sits under the header checkbox.
              <div className="flex items-center justify-center gap-2 w-full">
                <Checkbox
                  checked={row.original.in_app_enabled}
                  onChange={(e) =>
                    handleCheckOne(row.original.preference_slug, {
                      inAppEnabled: e.target.checked,
                    })
                  }
                />
                <span className="invisible" aria-hidden>
                  In App
                </span>
              </div>
            )
          }
          return null
        },
      },
    ]
  }, [rawData])

  if (isLoading || !rawData) {
    return (
      <div className="flex justify-center items-center h-[500px] w-full">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <TableLinkStyleProvider variant="plain">
      <div className="notification-prefs-table flex flex-col w-full flex-1 min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
        <DataTableContainer className="h-full">
          <ClientDataTable
            height="100%"
            tableHeader={
              <div
                data-table-toolbar
                className="flex flex-row items-center gap-3 px-4 py-3"
              >
                <div className="relative w-full max-w-md">
                  <PiMagnifyingGlass
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search notifications..."
                    className="w-full h-9 pl-9 pr-3 text-sm bg-white border border-gray-300 rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-brand-active focus:shadow-[0_0_0_4px_rgba(8,116,67,0.2)]"
                  />
                </div>
              </div>
            }
            data={data || []}
            columns={columns}
            stickyLastColumn
            rowHeight={44}
            headerHeight={40}
          />
        </DataTableContainer>
      </div>
    </TableLinkStyleProvider>
  )
}
